import { useMemo } from "react";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  toCreateEmploymentReq,
  toUpdateEmploymentReq,
  type EmploymentFormData,
} from "src/api/adapters/employment";
import {
  EmploymentFields,
  FormTable,
  FormTableBody,
  WorkHistoryIcon,
} from "@porchpass/ui";
import { DomainIncomeType } from "@porchpass/api-client";
import {
  applyEmploymentPatch,
  employmentErrorsAfterSubmit,
  toUiEmployment,
} from "src/forms/adapters/employment";
import { googlePlacesFieldProps } from "src/constants/googlePlaces";
import {
  notEmpty,
  lessThanMaxLength,
  validStreetAddress,
  validZipcode,
  validate,
  test,
  type ValidationMapperFunc,
  greaterThanMinAmount,
  validDateRange,
  isPastOrCurrentDate,
  lessThanMaxAmount,
} from "src/utils";

const getUpdatedValidationMapper = (showIncome: {
  bonus: boolean;
  commission: boolean;
  overtime: boolean;
}): ValidationMapperFunc<EmploymentFormData> => {
  return (formData) => ({
    employer_name: [
      test(notEmpty, "Employer name must not be empty"),
      test(
        lessThanMaxLength(70),
        "Employer name must not exceed 70 characters",
      ),
    ],
    position_title: [
      test(notEmpty, "Position must not be empty"),
      test(
        lessThanMaxLength(70),
        "Position title must not exceed 70 characters",
      ),
    ],
    street1: [
      test(notEmpty, "Address must not be empty"),
      test(validStreetAddress, "Must be a valid street address"),
      test(lessThanMaxLength(120), "Address must not exceed 120 characters"),
    ],
    city: [
      test(notEmpty, "City must not be empty"),
      test(lessThanMaxLength(70), "City must not exceed 70 characters"),
    ],
    state: [test(notEmpty, "State must not be empty")],
    zip_code: [
      test(notEmpty, "Zip Code must not be empty"),
      test(validZipcode, "Must be a valid US zip code"),
    ],
    frequency: [test(notEmpty, "Frequency must not be empty")],
    amount: [
      test(notEmpty, "Amount must not be empty"),
      test(greaterThanMinAmount(0), "Amount must be greater than 0"),
      test(lessThanMaxLength(15), "Amount must not exceed 15 characters"),
    ],
    from_month: [test(notEmpty, "Must select a month")],
    from_year: [
      test(notEmpty, "Must select a year"),
      test(
        () =>
          isPastOrCurrentDate(
            formData.from_month ?? "",
            formData.from_year ?? "",
          ),
        "Start date cannot be in the future",
      ),
    ],
    to_month: [
      test(
        (val) => Boolean(formData.is_current) || notEmpty(val),
        "Must select a month",
      ),
    ],
    to_year: [
      test(
        (val) => Boolean(formData.is_current) || notEmpty(val),
        "Must select a year",
      ),
      test(
        () =>
          Boolean(formData.is_current) ||
          validDateRange(
            formData.from_month ?? "",
            formData.from_year ?? "",
            formData.to_month ?? "",
            formData.to_year ?? "",
          ),
        "End date must be after start date",
      ),
    ],
    weekly_hours:
      formData.frequency === "HOURLY"
        ? [
            test(notEmpty, "Weekly Hours must not be empty"),
            test(
              greaterThanMinAmount(0),
              "Weekly hours must be greater than 0",
            ),
            test(lessThanMaxAmount(168), "Weekly hours must be less than 168"),
          ]
        : [],
    ...(showIncome.bonus
      ? {
          bonus_amount: [
            test(notEmpty, "Bonus must not be empty"),
            test(greaterThanMinAmount(0), "Bonus must be greater than 0"),
          ],
          bonus_frequency: [test(notEmpty, "Frequency must not be empty")],
          bonus_weekly_hours:
            formData.bonus_frequency === "HOURLY"
              ? [
                  test(notEmpty, "Bonus weekly Hours must not be empty"),
                  test(
                    greaterThanMinAmount(0),
                    "Bonus weekly hours must be greater than 0",
                  ),
                  test(
                    lessThanMaxAmount(168),
                    "Bonus weekly hours must be less than 168",
                  ),
                ]
              : [],
        }
      : {}),
    ...(showIncome.commission
      ? {
          commission_amount: [
            test(notEmpty, "Commission must not be empty"),
            test(greaterThanMinAmount(0), "Commission must be greater than 0"),
          ],
          commission_frequency: [test(notEmpty, "Frequency must not be empty")],
          commission_weekly_hours:
            formData.commission_frequency === "HOURLY"
              ? [
                  test(notEmpty, "Commission weekly Hours must not be empty"),
                  test(
                    greaterThanMinAmount(0),
                    "Commission weekly hours must be greater than 0",
                  ),
                  test(
                    lessThanMaxAmount(168),
                    "Commission weekly hours must be less than 168",
                  ),
                ]
              : [],
        }
      : {}),
    ...(showIncome.overtime
      ? {
          overtime_amount: [
            test(notEmpty, "Overtime must not be empty"),
            test(greaterThanMinAmount(0), "Overtime must be greater than 0"),
          ],
          overtime_weekly_hours: [
            test(notEmpty, "Overtime weekly Hours must not be empty"),
            test(
              greaterThanMinAmount(0),
              "Overtime weekly hours must be greater than 0",
            ),
            test(
              lessThanMaxAmount(168),
              "Overtime weekly hours must be less than 168",
            ),
          ],
        }
      : {}),
  });
};

type EmploymentFormModalProps = {
  employment_detail?: EmploymentFormData;
  closeHandler: () => void;
  confirmHandler: () => void;
  hideCurrentCheckbox?: boolean;
};

export default ({
  employment_detail,
  closeHandler,
  confirmHandler,
  hideCurrentCheckbox,
}: EmploymentFormModalProps) => {
  const api = useWebClientApi();
  const employmentId = employment_detail?.id;
  const isCreate = !employmentId || employmentId === "CREATE";

  const { formData, setFormData } = useFormLoad<EmploymentFormData>(
    { ...employment_detail },
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );
  const [submitHandler, loading, , hasAttemptedFormSubmission] =
    useDlaFormSubmit(
      async (data: EmploymentFormData) => {
        if (isCreate) {
          return api.createEmployment(toCreateEmploymentReq(data));
        }
        return api.updateEmployment(employmentId, toUpdateEmploymentReq(data));
      },
      { onSuccess: () => confirmHandler() },
    );

  const employmentValue = useMemo(() => toUiEmployment(formData), [formData]);

  const showIncome = useMemo(
    () => ({
      bonus: employmentValue.incomes.some(
        (inc) => inc.incomeType === DomainIncomeType.IncomeBonus,
      ),
      commission: employmentValue.incomes.some(
        (inc) => inc.incomeType === DomainIncomeType.IncomeCommission,
      ),
      overtime: employmentValue.incomes.some(
        (inc) => inc.incomeType === DomainIncomeType.IncomeOvertime,
      ),
    }),
    [employmentValue.incomes],
  );

  const validationMapper = getUpdatedValidationMapper(showIncome);
  const formErrors = validate<EmploymentFormData>(formData, validationMapper);

  const { employmentErrors, employerAddressErrors } = useMemo(
    () => employmentErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyEmploymentChange = (
    patch: Parameters<typeof applyEmploymentPatch>[1],
  ) => {
    setFormData((prev) => applyEmploymentPatch(prev, patch));
  };

  return (
    <SlideUpModal
      title="Tell us about your employer"
      isOpen={true}
      openSession={employment_detail}
      closeHandler={closeHandler}
      actionButtonText={employment_detail?.id ? "Save" : "Add Employment"}
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Employment Details"
        headerIcon={
          <WorkHistoryIcon
            variant="bulk-rounded"
            size={16}
            className="text-[#6F41E6]"
          />
        }
      >
        <FormTableBody>
          <EmploymentFields
            value={employmentValue}
            errors={employmentErrors}
            employerAddressErrors={employerAddressErrors}
            hideCurrentToggle={hideCurrentCheckbox}
            onChange={applyEmploymentChange}
            {...googlePlacesFieldProps}
          />
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
